// Validation + reward helper for the SOS preparedness badge (Fase 8).
//
// Flow:
//   1. The SOS Identity Card calls `requestPreparednessReward` after a
//      successful local save. It handles ALL the toast / claim logic.
//   2. On app mount (post-login) and on every `online` event, the global
//      retry listener (installed in AuthContext) replays any pending claim.
//
// localStorage keys:
//   - sos_identity_card           : the card itself (owned by SOSIdentityCard)
//   - preparedness_reward_pending : "1" if we need to retry a claim later
//   - reward_notified_v1          : "1" if we've shown the success toast once
//
// Backend:
//   POST /api/sos/claim-preparedness-reward (JWT) →
//     { ok: bool, credited: bool, balance: int }
//   - `credited: true` only on the very first grant for the user.

import { toast } from "sonner";
import { api } from "./api";
import { safeGetItem, safeRemoveItem, safeSetItem } from "./sosStorage";

const CARD_KEY = "sos_identity_card";
const PENDING_KEY = "preparedness_reward_pending";
const NOTIFIED_KEY = "reward_notified_v1";

/* -------------------- Validation -------------------- */

function _countDigits(s) {
  return (s || "").replace(/\D/g, "").length;
}

/**
 * Validate the preparedness card against the Fase 8 contract:
 *   - fullName (card.name): >= 3 chars after trim
 *   - emergencyContact (card.emergency_contact): >= 7 numeric digits (ignores
 *     +, spaces, hyphens)
 *
 * Returns an object { ok, errors: { name?, emergency_contact? } }. The errors
 * map keys match the SOSIdentityCard field names.
 */
export function validatePreparednessCard(card) {
  const errors = {};
  const name = (card?.name || "").trim();
  if (name.length < 3) {
    errors.name = "Ingresa tu nombre completo (mínimo 3 caracteres).";
  }
  const digits = _countDigits(card?.emergency_contact);
  if (digits < 7) {
    errors.emergency_contact =
      "Ingresa un teléfono de contacto con al menos 7 dígitos.";
  }
  return { ok: Object.keys(errors).length === 0, errors };
}

/** True iff the stored card currently passes validation. */
export function isStoredCardValid() {
  const raw = safeGetItem(CARD_KEY, null);
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw);
    return validatePreparednessCard(parsed).ok;
  } catch (_e) {
    return false;
  }
}

/* -------------------- Toasts (EXACT text per brief) -------------------- */

const T_GUEST =
  "Ficha guardada localmente. Inicia sesión para reclamar +10 NexaTokens.";
const T_RETRY =
  "Ficha guardada. La recompensa de tokens se procesará al reconectar.";
const T_SUCCESS =
  "¡Felicidades! Ganaste +10 NexaTokens (puntos de la app) por preparar tu ficha de emergencia.";

/* -------------------- Claim calls -------------------- */

async function _callClaim() {
  // Returns { credited, balance } on 200 or throws otherwise.
  const { data } = await api.post("/sos/claim-preparedness-reward");
  return data;
}

/**
 * Called by the SOSIdentityCard right after a validated local save.
 * Branches on auth / network state and handles all the toast rules.
 *
 * Never throws. Returns a summary object for the caller (tests + telemetry).
 */
export async function requestPreparednessReward({ user, isOnline = true } = {}) {
  // Guest branch — no API call.
  if (!user || !user.id) {
    safeSetItem(PENDING_KEY, "1");
    toast.info(T_GUEST);
    return { outcome: "guest" };
  }

  const alreadyNotified = safeGetItem(NOTIFIED_KEY, null) === "1";

  if (!isOnline || (typeof navigator !== "undefined" && navigator.onLine === false)) {
    // Online event listener will retry later.
    safeSetItem(PENDING_KEY, "1");
    toast.info(T_RETRY);
    return { outcome: "offline" };
  }

  try {
    const data = await _callClaim();
    safeRemoveItem(PENDING_KEY);
    if (data?.credited === true && !alreadyNotified) {
      toast.success(T_SUCCESS);
      safeSetItem(NOTIFIED_KEY, "1");
    } else if (data?.credited === false && !alreadyNotified) {
      // Already granted on the server (prior device / older session). Mark
      // notified silently to avoid spamming the user on later saves.
      safeSetItem(NOTIFIED_KEY, "1");
    }
    return { outcome: "credited", credited: !!data?.credited, balance: data?.balance };
  } catch (_err) {
    // Non-200 / network error → mark pending for retry on next `online`.
    safeSetItem(PENDING_KEY, "1");
    toast.info(T_RETRY);
    return { outcome: "retry_pending" };
  }
}

/**
 * Replay a pending claim. Called from AuthContext on login and on every
 * `online` event. Returns the result of the claim or null if nothing to do.
 */
export async function retryPendingPreparednessReward({ user, onBalanceUpdate } = {}) {
  if (!user || !user.id) return null;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return null;

  // Only retry if we have a stored, valid card AND either a pending flag OR
  // we never notified the user (second condition handles the case where the
  // user saved on a previous device without network — the server might
  // already have granted elsewhere).
  const pending = safeGetItem(PENDING_KEY, null) === "1";
  const notified = safeGetItem(NOTIFIED_KEY, null) === "1";
  if (!pending && notified) return null;
  if (!isStoredCardValid()) return null;

  try {
    const data = await _callClaim();
    safeRemoveItem(PENDING_KEY);
    if (data?.credited === true && !notified) {
      toast.success(T_SUCCESS);
      safeSetItem(NOTIFIED_KEY, "1");
    } else if (data?.credited === false && !notified) {
      safeSetItem(NOTIFIED_KEY, "1");
    }
    if (typeof onBalanceUpdate === "function" && typeof data?.balance === "number") {
      onBalanceUpdate(data.balance);
    }
    return data;
  } catch (_err) {
    // Keep pending flag for the next retry.
    return null;
  }
}

/* -------------------- Install the global retry listener -------------------- */

/**
 * Attach a global `online` listener that replays the pending claim. Returns
 * the cleanup function. Caller passes the current user object so we skip
 * the retry while logged out.
 */
export function installPreparednessRetryListener(getUser, onBalanceUpdate) {
  const handler = () => {
    const user = typeof getUser === "function" ? getUser() : getUser;
    if (!user) return;
    retryPendingPreparednessReward({ user, onBalanceUpdate }).catch(() => {});
  };
  window.addEventListener("online", handler);
  return () => window.removeEventListener("online", handler);
}

/** Exposed for tests. */
export const _storageKeys = { CARD_KEY, PENDING_KEY, NOTIFIED_KEY };
